import { useMemo } from 'react';
import type { SalesOrderV3Detail } from '@erp/rpc';
import { useGetProductMarginsByVariantIds } from '@/api/inventory-management/useGetProductMarginsByVariantIds';
import {
  aspForYear,
  deriveRow,
  deriveTotals,
  type DerivedRow,
  type Totals,
} from '@/pages/authenticated/quote-builder/marginCalc';

function toNum(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

/**
 * Margin data for a v3 draft, ported from v2's product-margins flow: cost,
 * target margin and prior-year ASP are fetched at render time from the
 * product-margins RPC keyed by `selectedProductVariantId` (the draft line
 * carries no cost). Reuses `marginCalc` so the per-line table and the metrics
 * row reconcile. Pass `enabled: false` (e.g. no margin permission) to skip the
 * fetch entirely; `totals` is then `undefined` and margin metrics render "—".
 */
export function useDraftMargin(
  draft: SalesOrderV3Detail | undefined,
  enabled: boolean,
) {
  const lineItems = draft?.lineItems;
  const variantIds = useMemo(
    () =>
      (lineItems ?? [])
        .map((li) => li.selectedProductVariantId)
        .filter((id): id is string => !!id),
    [lineItems],
  );

  const {
    marginByVariantId,
    yearsCovered,
    hasProductMargins,
    salesScope,
    isLoading,
  } = useGetProductMarginsByVariantIds(variantIds, {
    enabled: enabled && !!draft,
    customerId: draft?.customerId ?? undefined,
  });

  const derivedRows = useMemo<DerivedRow[]>(() => {
    if (!enabled || !lineItems) return [];
    const prevYear = yearsCovered[1]; // year before the most recent covered year
    return lineItems
      .filter((li) => li.selectedProductVariantId)
      .map((li) => {
        const variantId = li.selectedProductVariantId as string;
        const marginRow = marginByVariantId.get(variantId);
        const row = deriveRow(
          {
            uid: li.id,
            productVariantId: variantId,
            unitPrice: toNum(li.unitPrice) ?? 0,
            quantity: toNum(li.quantity) ?? 0,
          },
          marginRow,
          toNum(li.listPrice),
          aspForYear(marginRow, prevYear),
        );
        // Show the customer's PO identity (same as the Line items tab) instead
        // of the matched variant's catalog sku/description; the catalog
        // identity stays as the fallback for lines missing customer fields.
        return {
          ...row,
          sku: li.customerIdentifier ?? row.sku,
          description: li.customerDescription ?? row.description,
        };
      });
  }, [enabled, lineItems, marginByVariantId, yearsCovered]);

  // Only expose totals once margin rows are loaded; the totals' own null fields
  // (e.g. totalMarginPct) signal "no cost data" per metric.
  const totals = useMemo<Totals | undefined>(
    () =>
      enabled && marginByVariantId.size > 0
        ? deriveTotals(derivedRows)
        : undefined,
    [enabled, marginByVariantId, derivedRows],
  );

  return {
    derivedRows,
    totals,
    yearsCovered,
    hasProductMargins,
    /** How the regional series was scoped (price-list peers vs whole org). */
    salesScope,
    isLoading,
  };
}
